/**
 * stato-meta.ts — F-005 Phase 4 (IMPL-P4-003).
 *
 * Single source of truth per la presentazione UI degli stati pratica:
 *   - label IT-IT
 *   - icona Lucide (richiesta WCAG: redundant color + icon)
 *   - classi Tailwind per il badge (background + foreground)
 *   - aria-label esteso per screen reader
 *   - flag `isTerminal` consumato dai polling hook per fermare il refetch
 *
 * `StatoPratica` è importato dal client locale (non da @app/shared) — è una
 * union literal allineata all'enum Prisma `PraticaStato`. `ARCHIVIATA` è
 * incluso come stato futuro (V2 / F-006).
 */
import type { ComponentType } from 'react';
import {
  FileEdit,
  Send,
  Hammer,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Archive,
  XCircle,
} from 'lucide-react';
import type { StatoPratica } from './cliente-pratiche-client';

export interface StatoMeta {
  label: string;
  /** Tailwind classes per il badge (background + foreground). */
  badgeClass: string;
  icon: ComponentType<{ className?: string }>;
  ariaLabel: string;
  /**
   * `true` per stati terminali (non più aggiornabili). I polling hook
   * fermano il refetch quando ALL items / il pratica corrente sono terminal.
   */
  isTerminal: boolean;
}

export const STATO_META: Record<StatoPratica, StatoMeta> = {
  BOZZA: {
    label: 'Bozza',
    badgeClass: 'bg-gray-100 text-gray-700',
    icon: FileEdit,
    ariaLabel: 'Pratica in bozza',
    isTerminal: false,
  },
  INVIATA: {
    label: 'Inviata',
    badgeClass: 'bg-blue-100 text-blue-700',
    icon: Send,
    ariaLabel: 'Pratica inviata',
    isTerminal: false,
  },
  IN_CARICO: {
    label: 'In carico',
    badgeClass: 'bg-yellow-100 text-yellow-700',
    icon: Hammer,
    ariaLabel: "Pratica in carico all'operatore",
    isTerminal: false,
  },
  IN_ELABORAZIONE: {
    label: 'In elaborazione',
    badgeClass: 'bg-purple-100 text-purple-700',
    icon: RefreshCw,
    ariaLabel: 'Pratica in elaborazione',
    isTerminal: false,
  },
  BLOCCATA: {
    label: 'Bloccata',
    badgeClass: 'bg-red-100 text-red-700',
    icon: AlertTriangle,
    ariaLabel: 'Pratica bloccata — azione richiesta',
    isTerminal: false,
  },
  COMPLETATA: {
    label: 'Completata',
    badgeClass: 'bg-green-100 text-green-700',
    icon: CheckCircle2,
    ariaLabel: 'Pratica completata',
    isTerminal: true,
  },
  ANNULLATA: {
    label: 'Annullata',
    badgeClass: 'bg-gray-200 text-gray-500',
    icon: XCircle,
    ariaLabel: 'Pratica annullata',
    isTerminal: true,
  },
  ARCHIVIATA: {
    label: 'Archiviata',
    badgeClass: 'bg-gray-200 text-gray-500',
    icon: Archive,
    ariaLabel: 'Pratica archiviata',
    isTerminal: true,
  },
} as const;

/**
 * Helper: `true` quando lo stato non genera più transizioni e i polling
 * hook devono fermarsi (`refetchInterval` → `false`).
 */
export const isTerminalStato = (stato: StatoPratica): boolean =>
  STATO_META[stato]?.isTerminal ?? false;
